import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, PenLine, MapPin, KeyRound, X, CheckCircle, AlertCircle } from 'lucide-react';
import { createApiClient, createEmployeePortalClient, api } from '../../services/api';

interface Props {
  jobId: string;
  onClose: () => void;
  onSuccess: () => void;
  /** When provided (employee portal context), requests go through the portal endpoint with this token */
  portalToken?: string;
  /** Required for non-portal (carrier) context to attach Bearer auth */
  getToken?: () => Promise<string>;
  /** When true (repair job), relaxes validation — only photos required; skips GPS/signature */
  isRepairJob?: boolean;
}

interface GpsPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export function PodSubmissionModal({ jobId, onClose, onSuccess, portalToken, getToken, isRepairJob }: Props) {
  const portalClient   = portalToken ? createEmployeePortalClient(portalToken) : null;
  const imageEndpoint  = portalClient ? `/api/employee-portal/jobs/${jobId}/images` : `/api/jobs/${jobId}/images`;
  const podEndpoint    = portalClient ? `/api/employee-portal/jobs/${jobId}/pod`    : `/api/jobs/${jobId}/pod`;
  const httpClient     = portalClient ?? (getToken ? createApiClient(getToken) : api);
  // ── Signature ─────────────────────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const lastPt = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    lastPt.current = getPos(e, canvas);
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas || !lastPt.current) return;
    const ctx = canvas.getContext('2d')!;
    const pt = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPt.current = pt;
    setHasSig(true);
  }, [isDrawing]);

  const endDraw = useCallback(() => setIsDrawing(false), []);

  const clearSig = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
  };

  // Fill canvas white on first render so the background is never transparent
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // ── Photos / videos ──────────────────────────────────────────────────────────
  const [photos, setPhotos] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const isVideoDataUrl = (dataUrl: string) => /^data:video\//.test(dataUrl);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx: number) =>
    setPhotos((prev) => prev.filter((_, i) => i !== idx));

  // ── GPS ────────────────────────────────────────────────────────────────────
  const [gps, setGps] = useState<GpsPosition | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const captureGps = () => {
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setGpsLoading(false);
      },
      (err) => {
        // getCurrentPosition's error callback fires for PERMISSION_DENIED (1), POSITION_UNAVAILABLE
        // (2), and TIMEOUT (3) alike - showing "access denied" for all three is actively
        // misleading for the timeout case, which just needs a retry rather than an OS-level
        // permissions change the message told the user to make.
        setGpsError(
          err.code === err.PERMISSION_DENIED
            ? 'Location access denied. Enable location services and try again.'
            : 'Could not get your location in time. Try again.'
        );
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 20_000 }
    );
  };

  useEffect(() => { captureGps(); }, []);

  // ── Form fields ────────────────────────────────────────────────────────────
  const [recipientName, setRecipientName] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [repairNotes, setRepairNotes] = useState('');

  // ── Submission ─────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = () => {
    if (photos.length === 0) return 'At least one photo or video is required.';
    if (!isRepairJob) {
      if (!hasSig) return 'Customer signature is required.';
      if (!recipientName.trim()) return 'Recipient name is required.';
      if (!gps) return 'GPS location is required. Please allow location access.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setSubmitError(err); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Upload all delivery photos
      const imagePayload = photos.map((dataUrl) => {
        const [meta, base64] = dataUrl.split(',');
        const mimeType = meta.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
        return { base64, mimeType };
      });
      const { data: { urls } } = await httpClient.post<{ urls: string[] }>(
        imageEndpoint, { images: imagePayload }
      );

      let sigUrl: string | null = null;
      if (!isRepairJob) {
        // Upload signature
        const canvas = canvasRef.current!;
        const [, sigBase64] = canvas.toDataURL('image/png').split(',');
        const { data: { urls: sigUrls } } = await httpClient.post<{ urls: string[] }>(
          imageEndpoint,
          { images: [{ base64: sigBase64, mimeType: 'image/png' }] }
        );
        sigUrl = sigUrls[0];
      }

      // Submit POD with all photo URLs
      await httpClient.post(podEndpoint, {
        photoUrls:            urls,
        customerSignatureUrl: sigUrl,
        recipientName:        isRepairJob ? null : recipientName,
        gpsLatitude:          isRepairJob ? null : gps!.latitude,
        gpsLongitude:         isRepairJob ? null : gps!.longitude,
        gpsAccuracyMetres:    isRepairJob ? null : gps!.accuracy,
        otpCode:              isRepairJob ? null : otpCode,
        notes:                isRepairJob ? repairNotes : null,
      });

      onSuccess();
    } catch (e: any) {
      setSubmitError(e.response?.data?.error ?? e.message ?? 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <h2 className="font-semibold text-gray-900 dark:text-white text-lg">Submit Proof of Delivery</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* GPS — delivery jobs only */}
          {!isRepairJob && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-800 dark:text-gray-100 text-sm">GPS Location</h3>
              {gps && <CheckCircle className="w-4 h-4 text-green-500" />}
            </div>
            {gps ? (
              <p className="text-xs text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 font-mono">
                {gps.latitude.toFixed(6)}, {gps.longitude.toFixed(6)}
                <span className="ml-2 text-gray-400 dark:text-gray-500">(±{Math.round(gps.accuracy)}m)</span>
              </p>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={captureGps}
                  disabled={gpsLoading}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium disabled:opacity-50"
                >
                  {gpsLoading ? 'Locating…' : 'Capture Location'}
                </button>
                {gpsError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {gpsError}
                  </p>
                )}
              </div>
            )}
          </section>
          )}

          {/* Photos / videos — mandatory */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-800 dark:text-gray-100 text-sm">
                Delivery Photos / Videos <span className="text-red-500">*</span>
              </h3>
            </div>
            <input
              ref={fileRef}
              id="pod-gallery-input"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photos.map((src, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden aspect-square bg-gray-100 dark:bg-gray-700">
                    {isVideoDataUrl(src) ? (
                      <video src={src} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    )}
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-black/50 rounded-full p-0.5 text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label
              htmlFor="pod-gallery-input"
              className="flex items-center gap-2 text-sm border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 rounded-lg px-3 py-3 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 justify-center transition-colors cursor-pointer"
            >
              <Camera className="w-4 h-4" />
              Add Photos / Videos
            </label>
          </section>

          {/* Signature — delivery jobs only */}
          {!isRepairJob && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <PenLine className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="font-medium text-gray-800 dark:text-gray-100 text-sm">
                  Customer Signature <span className="text-red-500">*</span>
                </h3>
              </div>
              {hasSig && (
                <button onClick={clearSig} className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400">
                  Clear
                </button>
              )}
            </div>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-xl overflow-hidden" style={{ background: '#ffffff' }}>
              <canvas
                ref={canvasRef}
                data-testid="pod-signature-canvas"
                width={450}
                height={160}
                className="w-full touch-none cursor-crosshair"
                style={{ background: '#ffffff' }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
            </div>
            {!hasSig && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
                Ask the recipient to sign above
              </p>
            )}
          </section>
          )}

          {/* Recipient name — delivery jobs only */}
          {!isRepairJob && (
          <section>
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
              Recipient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Full name of person who received the delivery"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </section>
          )}

          {/* OTP — delivery jobs only */}
          {!isRepairJob && (
          <section>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-100">
                Delivery OTP <span className="text-gray-400 dark:text-gray-500 font-normal text-xs">(optional)</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              If the customer has generated a code in their app, enter it here. Leave blank if unavailable — support can verify separately.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit OTP"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-mono tracking-widest text-center text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </section>
          )}

          {/* Repair notes — repair jobs only */}
          {isRepairJob && (
          <section>
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
              Completion Notes <span className="text-gray-400 font-normal text-xs">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={repairNotes}
              onChange={(e) => setRepairNotes(e.target.value)}
              placeholder="Notes on work completed, parts used, recommendations, etc."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </section>
          )}

          {/* Error */}
          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {submitError}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
